<template>
    <div class="row">
        <div class="col-12">
            <div class="border-light b-radius-20 p-4 service-menus-options">
                <div class="row">
                    <div class="col-12">
                        <strong>{{ $tl("labels.booking.services.menusAndOptions.title", restaurantId) }}</strong>
                    </div>
                </div>
                <div class="menu-option mt-3">
                    <div class="menus-options-section">
                        <div class="menus-options-section__header">
                            <label class="form-label m-0">{{ $tl("labels.booking.services.menusAndOptions.link", restaurantId) }}</label>
                        </div>
                        <div class="menus-options-section__actions">
                            <button type="button" @click="showMenuPicker = !showMenuPicker" class="menus-options-action">
                                <feather type="plus" size="14" class="me-1" />
                                {{ $tl("labels.booking.services.menusAndOptions.addMenu", restaurantId) }}
                            </button>
                        </div>

                        <div v-if="showMenuPicker" class="menu-picker">
                            <div v-if="availableMenus.length > 0" class="menu-picker__list">
                                <button
                                    v-for="menu in availableMenus"
                                    :key="menu.id"
                                    type="button"
                                    class="menu-picker__item"
                                    @click="addMenu(menu.id)">
                                    <span class="menu-picker__associate">
                                        <feather type="plus" size="13" />
                                        {{ $tl("labels.booking.services.menusAndOptions.associate", restaurantId) }}
                                    </span>
                                    <span>{{ menu.name }}</span>
                                    <small>{{ $tl("labels.booking.services.menusAndOptions.inactive", restaurantId) }}</small>
                                </button>
                            </div>
                            <div v-else class="menus-empty-state menus-empty-state--compact">
                                {{ $tl("labels.booking.services.menusAndOptions.allMenusAssociated", restaurantId) }}
                            </div>
                            <button type="button" @click="showAddMenuModal = true" class="menus-options-action menus-options-action--secondary mt-3">
                                <feather type="plus" size="14" class="me-1" />
                                {{ $tl("labels.booking.services.menusAndOptions.createMenu", restaurantId) }}
                            </button>
                        </div>

                        <div v-if="selectedMenus.length > 0" class="menu-cards-grid">
                            <article v-for="menu in selectedMenus" :key="menu.id" class="menu-card">
                                <div class="menu-card__header">
                                    <div class="menu-card__title">
                                        <strong>{{ menu.name }}</strong>
                                        <span class="menu-state menu-state--enabled">
                                            {{ $tl("labels.booking.services.menusAndOptions.active", restaurantId) }}
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        class="menu-card__remove"
                                        :aria-label="`${$tl('labels.delete')} ${menu.name}`"
                                        @click="removeMenu(menu.id)">
                                        <feather type="x" size="16" />
                                    </button>
                                </div>
                                <div class="menu-card__rules">
                                    <div class="menu-rule">
                                        <label class="form-label container-box menu-rule__toggle">
                                            <input type="checkbox" v-model="currentMenus.checkedMenus[menu.id].enable_nb_min_pers" />
                                            <span class="checkmark"></span>
                                            <span>{{ $tl("labels.booking.services.menusAndOptions.enableFrom", restaurantId) }}</span>
                                        </label>
                                        <div v-if="currentMenus.checkedMenus[menu.id].enable_nb_min_pers" class="menu-rule__value">
                                            <input
                                                type="number"
                                                step="1"
                                                min="0"
                                                name="nb_pers_min"
                                                class="form-control"
                                                v-model="currentMenus.checkedMenus[menu.id].nb_min_pers" />
                                            <span>{{ $tl("labels.persons") }}</span>
                                        </div>
                                    </div>
                                    <div class="menu-rule">
                                        <label class="form-label container-box menu-rule__toggle">
                                            <input type="checkbox" v-model="currentMenus.checkedMenus[menu.id].enable_nb_max_pers" />
                                            <span class="checkmark"></span>
                                            <span>{{ $tl("labels.booking.services.menusAndOptions.disableFrom", restaurantId) }}</span>
                                        </label>
                                        <div v-if="currentMenus.checkedMenus[menu.id].enable_nb_max_pers" class="menu-rule__value">
                                            <input
                                                type="number"
                                                step="1"
                                                min="0"
                                                name="nb_pers_max"
                                                class="form-control"
                                                v-model="currentMenus.checkedMenus[menu.id].nb_max_pers" />
                                            <span>{{ $tl("labels.persons") }}</span>
                                        </div>
                                    </div>
                                </div>
                            </article>
                        </div>
                        <div v-else class="menus-empty-state">
                            {{ $tl("labels.booking.services.menusAndOptions.noAssociatedMenu", restaurantId) }}
                        </div>
                    </div>

                    <template v-if="selectedMenus.length > 0">
                        <div class="menus-options-section selection-rules">
                            <div class="menus-options-section__header">
                                <label class="form-label m-0">{{
                                    $tl("labels.booking.services.menusAndOptions.selectionRules", restaurantId)
                                }}</label>
                            </div>
                            <div class="selection-rules-grid">
                                <div class="selection-rule-card">
                                    <div class="selection-rule-card__top">
                                        <label class="form-label">
                                            {{ $tl("labels.booking.menus.mandatory", restaurantId) }}
                                            <feather
                                                v-tooltip="getTooltipNotice('notices.booking.servicesConfig.mandatoryMenu')"
                                                type="info"
                                                class="ms-1 pointer tooltip-infos" />
                                        </label>
                                        <ToggleInput v-model="currentMenus.config.isMandatory" input-id="menu-mandatory-switch" />
                                    </div>
                                    <div v-if="currentMenus.config.isMandatory" class="selection-rule-card__field">
                                        <span>{{ $tl("labels.booking.menus.mandatoryFrom", restaurantId) }}</span>
                                        <input
                                            type="number"
                                            name="menu_mandatory_from"
                                            v-model="currentMenus.config.nbPersMandatoryAbovePax"
                                            class="form-control"
                                            min="1"
                                            step="1" />
                                        <span>{{ $tl("labels.personss") }}</span>
                                    </div>
                                </div>
                                <div class="selection-rule-card">
                                    <div class="selection-rule-card__top">
                                        <label class="form-label">
                                            {{ $tl("labels.booking.menus.unique", restaurantId) }}
                                            <feather
                                                v-tooltip="getTooltipNoticeLexicon('notices.booking.servicesConfig.uniqueMenu', restaurantId)"
                                                type="info"
                                                class="ms-1 pointer tooltip-infos" />
                                        </label>
                                        <ToggleInput v-model="currentMenus.config.isUnique" input-id="menu-unique-switch" />
                                    </div>
                                    <div v-if="currentMenus.config.isUnique" class="selection-rule-card__field">
                                        <span>{{ $tl("labels.booking.menus.uniqueFrom", restaurantId) }}</span>
                                        <input
                                            type="number"
                                            name="menu_unique_from"
                                            v-model="currentMenus.config.nbPersUniqueAbovePax"
                                            class="form-control"
                                            min="1"
                                            step="1" />
                                        <span>{{ $tl("labels.personss") }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>

                    <div class="sepa"></div>
                    <div class="menus-options-section">
                        <div class="menus-options-section__header">
                            <label class="form-label m-0">{{ $tl("labels.booking.services.generalOptions.link") }}</label>
                        </div>
                        <div class="menus-options-section__actions">
                            <button type="button" @click="showGeneralOptionPicker = !showGeneralOptionPicker" class="menus-options-action">
                                <feather type="plus" size="14" class="me-1" />
                                {{ $tl("labels.booking.services.menusAndOptions.addGeneralOption", restaurantId) }}
                            </button>
                        </div>

                        <div v-if="showGeneralOptionPicker" class="menu-picker">
                            <div v-if="availableGeneralOptions.length > 0" class="menu-picker__list">
                                <button
                                    v-for="option in availableGeneralOptions"
                                    :key="option.id"
                                    type="button"
                                    class="menu-picker__item"
                                    @click="addGeneralOption(option.id)">
                                    <span class="menu-picker__associate">
                                        <feather type="plus" size="13" />
                                        {{ $tl("labels.booking.services.menusAndOptions.associate", restaurantId) }}
                                    </span>
                                    <span>{{ option.name }}</span>
                                    <small>{{ $tl("labels.booking.services.menusAndOptions.inactive", restaurantId) }}</small>
                                </button>
                            </div>
                            <div v-else-if="generalOptions.length > 0" class="menus-empty-state menus-empty-state--compact">
                                {{ $tl("labels.booking.services.menusAndOptions.allGeneralOptionsAssociated", restaurantId) }}
                            </div>
                            <div v-else class="menus-empty-state menus-empty-state--compact">
                                {{ $tl("labels.booking.services.menusAndOptions.noGeneralOption", restaurantId) }}
                            </div>
                            <button
                                type="button"
                                @click="showAddGeneralOptionModal = true"
                                class="menus-options-action menus-options-action--secondary mt-3">
                                <feather type="plus" size="14" class="me-1" />
                                {{ $tl("labels.booking.services.menusAndOptions.createGeneralOption", restaurantId) }}
                            </button>
                        </div>

                        <div v-if="selectedGeneralOptions.length > 0" class="general-options-list">
                            <div v-for="option in selectedGeneralOptions" :key="option.id" class="general-options-list__item">
                                <span>{{ option.name }}</span>
                                <button
                                    type="button"
                                    class="menu-card__remove"
                                    :aria-label="`${$tl('labels.delete')} ${option.name}`"
                                    @click="removeGeneralOption(option.id)">
                                    <feather type="x" size="16" />
                                </button>
                            </div>
                        </div>
                        <div v-else class="menus-empty-state menus-empty-state--compact">
                            {{ $tl("labels.booking.services.menusAndOptions.noGeneralOption", restaurantId) }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <add-menu-modal v-if="showAddMenuModal" @menu-added="$emit('fetch-menus')" @close="showAddMenuModal = false" />
        <add-menu-option-modal
            v-if="showAddGeneralOptionModal"
            :option-type="MENU_OPTION_GENERAL.value"
            @menu-option-added="$emit('fetch-options')"
            @close="showAddGeneralOptionModal = false" />
    </div>
</template>

<script>
import AddMenuModal from "../../Modals/menus/addMenuModal.vue";
import addMenuOptionModal from "../../Modals/menuOptions/addMenuOptionModal.vue";
import MenuOptionTypeEnum from "../../../mixins/enums/booking/MenuOptionTypeEnum.js";
import ToggleInput from "../../forms/ToggleInput.vue";

export default {
    name: "MenuAndOptionsGroup",
    components: {
        AddMenuModal,
        addMenuOptionModal,
        ToggleInput,
    },
    data() {
        return {
            canEmitUpdate: false,

            showAddGeneralOptionModal: false,
            showAddMenuModal: false,
            showMenuPicker: false,
            showGeneralOptionPicker: false,

            currentMenus: {
                checkedMenus: {},
                config: {
                    isUnique: false,
                    nbPersUniqueAbovePax: 8,
                    isMandatory: false,
                    nbPersMandatoryAbovePax: null,
                },
            },

            checkedGeneralOptions: [],
        };
    },
    mixins: [MenuOptionTypeEnum],
    props: {
        menus: {
            type: Array,
            required: true,
        },
        generalOptions: {
            type: Array,
            required: true,
        },
        service: {
            type: Object,
            required: true,
        },
    },
    computed: {
        restaurantId() {
            return this.$route.params.restaurant_id;
        },
        selectedMenus() {
            return this.menus.filter((menu) => this.isMenuSelected(menu));
        },
        availableMenus() {
            return this.menus.filter((menu) => !this.isMenuSelected(menu));
        },
        selectedGeneralOptions() {
            return this.generalOptions.filter((option) => this.isGeneralOptionSelected(option));
        },
        availableGeneralOptions() {
            return this.generalOptions.filter((option) => !this.isGeneralOptionSelected(option));
        },
    },
    methods: {
        emitEvent(eventName, val) {
            if (!this.canEmitUpdate) {
                return;
            }
            this.$emit(eventName, val);
        },
        isMenuSelected(menu) {
            return !!(this.currentMenus.checkedMenus && this.currentMenus.checkedMenus[menu.id]);
        },
        isGeneralOptionSelected(option) {
            return this.checkedGeneralOptions.includes(option.id);
        },
        addMenu(menuId) {
            this.$set(this.currentMenus.checkedMenus, menuId, {
                id: menuId,
                enable_nb_min_pers: false,
                nb_min_pers: null,
                enable_nb_max_pers: false,
                nb_max_pers: null,
            });

            if (this.availableMenus.length === 0) {
                this.showMenuPicker = false;
            }
        },
        removeMenu(menuId) {
            this.currentMenus.checkedMenus = _.omit(this.currentMenus.checkedMenus, menuId);
        },
        addGeneralOption(optionId) {
            if (this.checkedGeneralOptions.includes(optionId)) {
                return;
            }

            this.checkedGeneralOptions = [...this.checkedGeneralOptions, optionId];

            if (this.availableGeneralOptions.length === 0) {
                this.showGeneralOptionPicker = false;
            }
        },
        removeGeneralOption(optionId) {
            this.checkedGeneralOptions = this.checkedGeneralOptions.filter((checkedOptionId) => checkedOptionId !== optionId);
        },
        initMenus() {
            this.currentMenus.config.isMandatory = !!this.service.menus.mandatory;
            this.currentMenus.config.nbPersMandatoryAbovePax = this.service.menus.mandatoryAbovePax;
            this.currentMenus.config.isUnique = !!this.service.menus.mustBeUnique;
            this.currentMenus.config.nbPersUniqueAbovePax = this.service.menus.isUniqueAbovePax;
            this.currentMenus.checkedMenus = this.service.menus.value || {};
        },
        initGeneralOptions() {
            this.checkedGeneralOptions = this.service.generalOptions.value;
        },
    },
    mounted() {
        this.initMenus();
        this.initGeneralOptions();
        this.canEmitUpdate = true;
    },
    watch: {
        currentMenus: {
            handler(val) {
                this.emitEvent("update-menus", val);
            },
            deep: true,
        },
        checkedGeneralOptions: {
            handler(val) {
                this.emitEvent("update-general-options", val);
            },
            deep: true,
        },
    },
};
</script>

<style scoped>
.service-menus-options {
    min-width: 0;
}

.menus-options-section {
    margin-top: 18px;
}

.menus-options-section__header {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
    margin-bottom: 10px;
}

.menus-options-section__header label {
    color: #344055;
    font-weight: 700;
}

.menus-options-section__actions {
    display: flex;
    justify-content: flex-start;
    margin-bottom: 14px;
}

.menus-options-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 34px;
    padding: 0 12px;
    border: 1px solid #30a4b7;
    border-radius: 8px;
    background: #eef9fb;
    color: #247892;
    font-size: 12px;
    font-weight: 800;
    line-height: 1;
    white-space: nowrap;
}

.menus-options-action:hover,
.menus-options-action:focus {
    border-color: #247892;
    background: #dff5f9;
    color: #173b57;
    text-decoration: none;
}

.menus-options-action:focus {
    outline: 2px solid #8fb8e8;
    outline-offset: 2px;
}

.menus-options-action--secondary {
    border-color: #dbe4f0;
    background: #f7fbff;
    color: #344055;
}

.menus-options-action--secondary:hover,
.menus-options-action--secondary:focus {
    border-color: #30a4b7;
    background: #eef9fb;
    color: #173b57;
}

.menu-cards-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 14px;
}

.menu-card,
.selection-rule-card,
.menu-picker {
    border: 1px solid #dfe9f5;
    border-radius: 14px;
    background: #fff;
}

.menu-card {
    padding: 16px;
}

.menu-card__header,
.selection-rule-card__top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
}

.menu-card__title {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.menu-card__title strong {
    color: #17233c;
    overflow-wrap: anywhere;
}

.menu-state {
    display: inline-flex;
    align-items: center;
    min-height: 24px;
    padding: 2px 9px;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 700;
}

.menu-state--enabled {
    color: #0f743e;
    background: #e9f8ef;
}

.menu-card__remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 30px;
    width: 30px;
    height: 30px;
    padding: 0;
    border: 1px solid #f2c7cf;
    border-radius: 999px;
    color: #b34050;
    background: #fff5f7;
}

.menu-card__remove:hover {
    color: #8f2736;
    border-color: #e79aa7;
    background: #ffecef;
}

.menu-card__rules {
    display: grid;
    gap: 10px;
    margin-top: 14px;
}

.menu-rule {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
}

.menu-rule__toggle {
    flex: 1 1 auto;
    min-width: 175px;
    margin: 0;
    color: #344055;
    font-weight: 600;
}

.menu-rule__value {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex: 0 0 auto;
}

.menu-rule__value .form-control,
.selection-rule-card__field .form-control {
    width: 86px;
}

.menu-rule__value span,
.selection-rule-card__field span {
    color: #6d7890;
    white-space: nowrap;
}

.menus-empty-state {
    border: 1px dashed #d8e3f0;
    border-radius: 14px;
    padding: 16px;
    color: #8a96aa;
    background: #f9fbfe;
    font-weight: 600;
}

.menus-empty-state--compact {
    padding: 12px;
}

.menu-picker {
    padding: 14px;
    margin-top: 14px;
    background: #f9fbfe;
}

.menu-picker__list {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 10px;
}

.menu-picker__item {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 11px 12px;
    border: 1px solid #dfe9f5;
    border-radius: 12px;
    color: #17233c;
    background: #fff;
    text-align: left;
}

.menu-picker__associate {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    min-height: 28px;
    padding: 0 9px;
    border: 1px solid #9fd9c0;
    border-radius: 999px;
    background: #edf9f4;
    color: #176b4b;
    font-size: 11px;
    font-weight: 800;
    line-height: 1;
    white-space: nowrap;
}

.menu-picker__associate svg {
    flex: 0 0 auto;
}

.menu-picker__item:hover {
    border-color: #9fd9c0;
    background: #fbfefc;
}

.menu-picker__item span {
    min-width: 0;
    overflow-wrap: anywhere;
    font-weight: 700;
}

.menu-picker__item small {
    color: #8a96aa;
    font-weight: 700;
}

.selection-rules {
    padding-top: 4px;
}

.selection-rules-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 14px;
}

.selection-rule-card {
    padding: 16px;
    background: #f9fbfe;
}

.selection-rule-card label {
    margin-bottom: 0;
    color: #344055;
    font-weight: 700;
}

.selection-rule-card__field {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 9px;
    margin-top: 12px;
}

.general-options-list {
    display: grid;
    gap: 10px;
}

.general-options-list__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px;
    border: 1px solid #dfe9f5;
    border-radius: 12px;
    background: #fff;
    color: #17233c;
    font-weight: 700;
}

@media (max-width: 767.98px) {
    .menus-options-section__header,
    .menu-rule,
    .selection-rule-card__top {
        align-items: flex-start;
        flex-direction: column;
    }

    .menu-rule__value,
    .selection-rule-card__field {
        width: 100%;
    }

    .menu-rule__value .form-control,
    .selection-rule-card__field .form-control {
        width: 100%;
        max-width: 140px;
    }

    .menus-options-action {
        width: 100%;
    }

    .menu-picker__item {
        grid-template-columns: 1fr;
    }

    .menu-picker__associate {
        justify-self: start;
    }
}
</style>
